import React, { useEffect, useState, useRef } from "react";
import "./Selector.css";
import { IoChevronForward } from "react-icons/io5";
import { MdClear } from "react-icons/md";
import { themeColours } from "../../resources";

export function Selector({
  placeholder,
  listItems,
  handleSelectionChange,
  handleSelectionClear,
  selectedValue,
  isLoading,
  setSearch,
}: {
  placeholder: string;
  listItems: string[];
  selectedValue: string;
  handleSelectionChange: (update: any) => void;
  handleSelectionClear: () => void;
  unitClassName?: string;
  isLoading?: boolean;
  setSearch?: (search: string) => void;
}) {
  const [inputValue, setInputValue] = useState("");
  const [isFocus, setIsFocus] = useState(false);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const filteredItems = listItems.filter((item) => item.toLowerCase().includes(inputValue.toLowerCase()));
  const sortedItems = filteredItems.sort((a, b) => {
    const indexA = a.toLowerCase().indexOf(inputValue.toLowerCase());
    const indexB = b.toLowerCase().indexOf(inputValue.toLowerCase());
    return indexA - indexB || a.localeCompare(b);
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch ? setSearch(e.target.value) : setInputValue(e.target.value);
    if (e.target.value.length === 0) handleSelectionChange("");
  };

  const handleFocus = () => {
    setIsFocus(true);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsDropdownOpen(false);
      setIsInputVisible(false);
    }, 50);
    setIsFocus(false);
  };

  const handleClick = (item: string) => {
    handleSelectionChange(item);
    setInputValue(item);
    setIsDropdownOpen(false);
    setIsInputVisible(false);
    setIsFocus(false);
  };

  const handleClear = () => {
    setInputValue("");
    handleSelectionClear();
  };

  const handleIconClick = () => {
    if(!isFocus) {
      if(inputValue) handleClear()
      else handleDropdownToggle()
    }
  }

  // Handle the first click on the dropdown
  const handleDropdownToggle = () => {
    if (!isDropdownOpen) {
      // First click opens dropdown but doesn't show real input
      setIsDropdownOpen(true);
      setIsInputVisible(true);
      setIsFocus(true);
    } else {
      // Second click on the display area
      setIsInputVisible(true);
      // Focus after a slight delay to ensure the element is visible
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 10);
    }
  };

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setIsInputVisible(false);
        setIsFocus(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setInputValue(selectedValue);
  }, [selectedValue]);

  return (
    <div className="selectorContainer" ref={containerRef}>
      <div className={isFocus ? "inputClearSpanFocus" : "inputClearSpan"}>
        {/* Display element (visual input) - shown when real input is hidden */}
        {!isInputVisible && (
          <div
            className={`input ${inputValue ? "fakeInputValue" : "fakeInputPlaceholder"}`}
            onClick={handleDropdownToggle}
          >
            {inputValue.replace(/\b(blank|exclude|unassigned)\b/g, "N/A") || placeholder}
          </div>
        )}
        {isInputVisible && (
          <input
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={inputValue.replace(/\b(blank|exclude|unassigned)\b/g, "N/A")}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            className={"inputFocus"}
          />
        )}

        <div className={isFocus ? "inputClearFocus" : "inputClear"} onClick={handleIconClick}>
          {inputValue.length ? <MdClear /> : !isFocus ? <IoChevronForward color={themeColours.tertiary} /> : ""}
        </div>
      </div>

      {isDropdownOpen && !isLoading && (
        <div className="menuList">
          {filteredItems.length > 0 ? (
            sortedItems.map((item) => (
              <div
                key={item}
                onClick={() => handleClick(item)}
                className={item === selectedValue ? "menuItemSelected" : "menuItem"}
              >
                {item}
              </div>
            ))
          ) : (
            <div key={"none"} className={"menuItem"}>
              No result found
            </div>
          )}
        </div>
      )}

      {isLoading && (
        <div className="menuList">
          <div key={"none"} className={"menuItem"}>
            Searching...
          </div>
        </div>
      )}
    </div>
  );
}
